/**
 * What the editor currently has selected, for modules that are not on the
 * bootstrap path and cannot be handed the selection service.
 *
 * The multi-object selection lives in `MultiSelectionService`'s
 * `SelectionModel`, which `index.ts` owns and rebuilds whenever the viewer is
 * recreated. A module-level accessor like the other shared editor state
 * (`active-manager.ts`, the undo manager, the catalog token): `index.ts` sets
 * a getter, everyone else reads through it, and nothing has to reach into a
 * class instance it cannot see.
 *
 * A *getter* rather than the service itself, so the reader always sees the
 * live set and this module never has to know about the service's lifetime.
 */

import type { SceneObject } from 'trace-viewer/scene';

let _selected: (() => SceneObject[]) | null = null;

/** Called by `index.ts` when the selection service is created (and with null on teardown). */
export function setActiveSelectionSource(source: (() => SceneObject[]) | null): void {
    _selected = source;
}

/** Every currently selected object; empty when nothing is selected or no editor is up. */
export function getActiveSelection(): SceneObject[] {
    return _selected?.() ?? [];
}

/**
 * The objects a selection-driven action should act on, given the one object
 * the gesture named (a right-click pick, say).
 *
 * A right-click *inside* the current selection means the whole selection — the
 * rule every editor uses, and what makes "Save as asset" work on a
 * multi-select. A right-click outside it means only what was clicked; acting
 * on an invisible selection elsewhere in the scene is the surprise.
 */
export function selectionForAction(picked: SceneObject): SceneObject[] {
    const selection = getActiveSelection();
    return selection.includes(picked) ? selection : [picked];
}
